<template>
  <div class="flex-1 flex flex-col p-2 bg-white rounded-sm mt-2">
    <div class="flex flex-col animate-pulse bg-gray-100 min-h-screen" v-if="loading"></div>
    <div class="flex flex-col" v-else>
      <div class="flex flex-col" v-if="task">
        <div class="sticky top-0 bg-white z-10 pb-2 mb-2" style="margin: -0.5rem -0.5rem 0 -0.5rem; padding: 0.5rem 0.5rem 0 0.5rem; border-bottom: 1px solid #e5e7eb;">
          <b-row v-if="task">
            <b-col cols="12">
              <div class="flex justify-between items-center mb-3">
                <button
                  @click="goBack"
                  class="bg-gray rounded text-white hover:bg-gray-dark px-3 py-1 text-xs"
                >
                  <i class="fa fa-arrow-left mr-1"></i> Back to Forced Registrations
                </button>
                <h5 class="my-0 font-bold text-gray-700">Forced Registration Application Details</h5>
              </div>
              <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
                <thead class="text-md">
                <tr class="bg-gray-pale">
                  <td colspan="12" class="px-2 py-2 font-bold">Application Details</td>
                </tr>
                </thead>
                <tbody class="text-xs">
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold w-1/4">Tracking Number</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.applicationTrackingNumber || task.trackingNumber || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Entity Name</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.name || 'N/A' }}</td>
                </tr>
                <tr v-if="task.interimErn" style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Interim ERN</td>
                  <td class="px-2 py-1 text-capitalize text-blue">{{ task.interimErn || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Type</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.type || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Sub Type</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.subType || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Form</td>
                  <td class="px-2 py-1 text-capitalize">{{ (form && form.name) || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Associate Register</td>
                  <td class="px-2 py-1 text-capitalize">{{ institutionName || 'N/A' }}</td>
                </tr>
                </tbody>
              </table>
            </b-col>
          </b-row>
          <div v-if="canViewActionWithPermission('can_process_forced_registrations', 'forced_registrations')" class="flex gap-2 justify-end pb-2 mt-3" id="action-buttons">
            <button
                class="btn-classify"
                :disabled="transitioning"
                @click="openClassifyModal"
            >
              <i class="fas fa-tags text-white mr-1"></i> Classify
            </button>
            <button
                class="btn-approve"
                :disabled="transitioning"
                @click="openApproveModal"
            >
              <i class="fas fa-check-circle text-white mr-1"></i> Approve & Transition
            </button>
          </div>
        </div>
        <ForcedRegistrationDetails :id="taskId" class="mt-4" />
      </div>
      <div v-else class="flex items-center justify-center h-200 text-gray-500">
        <div class="flex flex-col text-center items-center py-12">
          <i class="fa fa-inbox text-4xl mb-2 text-gray-300"></i>
          <label>No task details found</label>
        </div>
      </div>
    </div>

    <!-- Approve & Transition Modal -->
    <b-modal id="modal-approve-transition" v-model="approveModalOpen" title="Approve & Transition Application" hide-footer size="md">
      <div class="p-1">
        <b-form @submit.prevent="saveApproval">
          <b-row class="mb-3">
            <b-col cols="12">
              <label class="text-sm font-semibold text-gray-700 mb-1">Associate Register</label>
              <select
                v-model="selectedInstitutionId"
                required
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
              >
                <option :value="null" disabled>-- Select Associate Register --</option>
                <option
                  v-for="inst in institutions"
                  :key="inst.id"
                  :value="inst.id"
                >
                  {{ inst.name }}
                </option>
              </select>
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col cols="12">
              <label class="text-sm font-semibold text-gray-700 mb-1">Primary Entity Type</label>
              <select
                v-model="selectedPrimaryTypeId"
                required
                @change="onPrimaryTypeChange"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
              >
                <option :value="null" disabled>-- Select Primary Entity Type --</option>
                <option
                  v-for="type in primaryEntityTypes"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.name }}
                </option>
              </select>
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col cols="12">
              <label class="text-sm font-semibold text-gray-700 mb-1">Sub Type</label>
              <select
                v-model="selectedSubTypeId"
                required
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
              >
                <option :value="null" disabled>-- Select Sub Type --</option>
                <option
                  v-for="type in subEntityTypes"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.name }}
                </option>
              </select>
            </b-col>
          </b-row>

          <b-row class="mt-4">
            <b-col cols="4">
              <button
                type="button"
                class="px-3 py-2 border-2 border-blue bg-white rounded-sm text-blue w-full text-sm font-semibold"
                @click="approveModalOpen = false"
              >
                Cancel
              </button>
            </b-col>
            <b-col cols="4"></b-col>
            <b-col cols="4">
              <button
                type="submit"
                :disabled="transitioning || !selectedInstitutionId || !selectedSubTypeId"
                class="px-3 py-2 bg-green rounded-sm text-white w-full hover:bg-green-dark text-sm font-semibold flex items-center justify-center"
              >
                <i class="fa fa-check-circle mr-1" v-if="!transitioning"></i>
                <i class="fa fa-spinner fa-spin mr-1" v-else></i>
                Approve
              </button>
            </b-col>
          </b-row>
        </b-form>
      </div>
    </b-modal>

    <!-- Classification / Link Modal -->
    <b-modal id="modal-classify" v-model="classifyModalOpen" title="Classify Application" hide-footer size="md">
      <div class="p-1">
        <b-form @submit.prevent="saveClassification">
          <b-row class="mb-3">
            <b-col cols="12">
              <label class="text-sm font-semibold text-gray-700 mb-1">Target Application Tracking Number</label>
              <input
                v-model="linkTrackingNumber"
                type="text"
                required
                placeholder="Enter tracking number to link"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p class="text-xs text-gray-400 mt-1">
                Enter the tracking number of the existing application to link to this forced registration.
              </p>
            </b-col>
          </b-row>
          <b-row class="mt-4">
            <b-col cols="4">
              <button
                type="button"
                class="px-3 py-2 border-2 border-blue bg-white rounded-sm text-blue w-full text-sm font-semibold"
                @click="classifyModalOpen = false"
              >
                Cancel
              </button>
            </b-col>
            <b-col cols="4"></b-col>
            <b-col cols="4">
              <button
                type="submit"
                :disabled="transitioning || !linkTrackingNumber"
                class="px-3 py-2 bg-green rounded-sm text-white w-full hover:bg-green-dark text-sm font-semibold flex items-center justify-center"
              >
                <i class="fa fa-save mr-1" v-if="!transitioning"></i>
                <i class="fa fa-spinner fa-spin mr-1" v-else></i>
                Save
              </button>
            </b-col>
          </b-row>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {api} from "@/app/services/api.service";
import ForcedRegistrationDetails from './ForcedRegistrationDetails.vue';

export default {
  name: 'ForcedRegistrationDetailsPage',
  components: { ForcedRegistrationDetails },
  data() {
    return {
      loading: true,
      transitioning: false,
      taskId: '',
      task: null,
      form: null,
      institutions: [],
      classifyModalOpen: false,
      linkTrackingNumber: '',
      approveModalOpen: false,
      selectedInstitutionId: null,
      selectedPrimaryTypeId: null,
      selectedSubTypeId: null,
      primaryEntityTypes: [],
      subEntityTypes: []
    };
  },
  computed: {
    institutionName() {
      if (!this.task || !this.task.institutionId) return 'N/A';
      const inst = (this.institutions || []).find(function (i) { return i.id === this.task.institutionId; }.bind(this));
      return inst ? inst.name : 'N/A';
    },
  },
  mounted() {
    this.taskId = this.$route.params.id;
    this.fetchInstitutions();
    this.fetchPrimaryEntityTypes();
    this.fetch();
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'non_individual_forced_registrations' });
    },
    canViewActionWithPermission(permission) {
      return this.$can(permission, "general") === true;
    },
    fetchInstitutions() {
      var self = this;
      api().get('/nir/api/v1/institutions?page=1&limit=1000')
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.content) {
              self.institutions = res.data.data.content;
            }
          })
          .catch(function () {});
    },
    fetchPrimaryEntityTypes() {
      var self = this;
      api().get('/nir/api/v1/configurations/entity-types/primary')
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.data) {
              self.primaryEntityTypes = res.data.data.data;
            } else {
              self.primaryEntityTypes = [];
            }
          })
          .catch(function () {
            self.primaryEntityTypes = [];
          });
    },
    fetchSubEntityTypes(parentId, callback) {
      var self = this;
      if (!parentId) {
        this.subEntityTypes = [];
        if (callback) callback();
        return;
      }
      api().get('/nir/api/v1/configurations/entity-types?parentId=' + encodeURIComponent(parentId))
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.data) {
              self.subEntityTypes = res.data.data.data;
            } else {
              self.subEntityTypes = [];
            }
            if (callback) callback();
          })
          .catch(function () {
            self.subEntityTypes = [];
            if (callback) callback();
          });
    },
    onPrimaryTypeChange() {
      this.selectedSubTypeId = null;
      this.fetchSubEntityTypes(this.selectedPrimaryTypeId);
    },
    fetch() {
      if (!this.taskId) {
        this.loading = false;
        return;
      }
      this.loading = true;
      var self = this;
      api().get('/nir/api/v1/applications/forced/task?id=' + encodeURIComponent(this.taskId))
          .then(function (res) {
            if (res.data && res.data.data) {
              self.task = res.data.data;
              if (self.task.formId) {
                self.fetchForm(self.task.formId);
              }
            } else {
              self.task = null;
            }
            self.loading = false;
          })
          .catch(function () {
            self.loading = false;
            self.task = null;
          });
    },
    fetchForm(formId) {
      var self = this;
      if (!formId) return;
      api().get('/nir/api/v1/configurations/form?id=' + encodeURIComponent(formId))
          .then(function (res) {
            if (res.data && res.data.data) self.form = res.data.data;
          });
    },
    openApproveModal() {
      this.selectedInstitutionId = this.task ? this.task.institutionId : null;
      this.selectedPrimaryTypeId = null;
      this.selectedSubTypeId = null;
      this.subEntityTypes = [];

      if (this.task && this.task.type) {
        const matchingPrimary = this.primaryEntityTypes.find(
          t => t.name.toLowerCase() === this.task.type.toLowerCase()
        );
        if (matchingPrimary) {
          this.selectedPrimaryTypeId = matchingPrimary.id;
          this.fetchSubEntityTypes(matchingPrimary.id, () => {
            if (this.task && this.task.subType) {
              const matchingSub = this.subEntityTypes.find(
                t => t.name.toLowerCase() === this.task.subType.toLowerCase()
              );
              if (matchingSub) {
                this.selectedSubTypeId = matchingSub.id;
              }
            }
          });
        }
      }
      this.approveModalOpen = true;
    },
    saveApproval() {
      if (!this.selectedInstitutionId || !this.selectedSubTypeId) return;
      this.transitioning = true;
      api().post('/nir/api/v1/applications/transition-forced-registration', {
        applicationId: this.taskId,
        institutionId: this.selectedInstitutionId,
        subTypeId: this.selectedSubTypeId,
        subType: this.selectedSubTypeId
      })
          .then(() => {
            this.transitioning = false;
            this.approveModalOpen = false;
            this.successToast('Success', 'Application approved and transitioned successfully');
            this.goBack();
          })
          .catch((error) => {
            this.transitioning = false;
            const message = error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : JSON.stringify(error);
            this.errorToast('Error', message);
          });
    },
    openClassifyModal() {
      this.linkTrackingNumber = '';
      this.classifyModalOpen = true;
    },
    saveClassification() {
      if (!this.linkTrackingNumber || this.linkTrackingNumber.trim() === '') return;
      this.transitioning = true;
      api().post('/nir/api/v1/applications/forced/link', {
        applicationId: this.taskId,
        trackingNumber: this.linkTrackingNumber.trim()
      })
          .then(() => {
            this.transitioning = false;
            this.classifyModalOpen = false;
            this.successToast('Success', 'Application linked and classified successfully');
            this.goBack();
          })
          .catch((error) => {
            this.transitioning = false;
            const message = error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : JSON.stringify(error);
            this.errorToast('Error', message);
          });
    },
  },
};
</script>

<style scoped>
@import "../../../../../assets/styles/table.css";

.btn-approve{
  @apply border bg-blue rounded-sm text-white text-sm px-3 py-1 mx-1 bg-white;
}

.btn-classify{
  @apply border bg-blue rounded-sm text-white text-sm px-3 py-1 mx-1 bg-white;
}

.btn-approve:hover{
  @apply bg-green border-green text-white;
}

.btn-classify:hover{
  @apply bg-orange border-orange text-white;
}

.flex-1 { flex: 1; }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.p-2 { padding: 0.5rem; }
.my-0 { margin-top: 0; margin-bottom: 0; }
</style>
